import { useCallback, useMemo, useRef, useState } from "react";
import { Body } from "./Body/Body";
import { HeaderGroup, Header } from "./Header/Header";
import { GenericColumnModel, FilterSortPageType, FilterType, InteractionManager, RemoteOptionColumnModel, RemoteOptionsColumnModel } from "./models";
import styles from './baseTable.module.scss'
import { Frozen } from "./Separators/Frozen";
import { Pagination } from "./Pagination/Pagination";
import { Resize } from "./Separators/Resize";
import { Filter } from "./Filter/Filter";
import { defaultPaginationLimit } from "./consts";
import { Footer } from "./Footer/Footer";
import { OverlaySpinner } from "components/Spinner";
import { Translation } from "components/Translations/Translation";
import { TableErrorsMap } from "../CrudTable/CrudTable";
import { useColumnWidths } from "./useColumnWidths";
import { useRemoteColumns } from "./useRemoteColumns";
import { cloneColumnModel } from "features/TableColumns/usePrepareTableColumns";
import { emptyArray } from "utils/commonHelper";

type Props = {
	columns: GenericColumnModel[]
	rowsData: any[]
	footerRowsData?: any[]
	cellEdited?: (oldRowData: any, columnId: string, newValue: any) => Promise<void>
	footerCellEdited?: (columnId: string, newValue: any) => Promise<void>
	/** Every next refetch so Spinner can be displayed */
	refetching?: boolean

	pagination?: {
		offset?: number // starting index
		limit?: number // max number of rows
		count: number // length of all items
		onChange: (offset: number) => void
	}
	compact?: boolean

	filterSortPage: FilterSortPageType
	disabledReorder: boolean

	/**
	 * - fill: Table will resize the columns and expand each column equally to fill up width of the table. However, if column has set width, it will keep that specific width.
	 * - If fill is not set, fitData will be used by default. Table will resize the columns to fit their data, and ensure that rows take up the full width of the table.
	*/
	fill?: boolean

	/** Render additional header row above the default header.
	 * This row includes grouped columns, with each group spanning width of the grouped columns.
	*/
	additionalHeaderRow?: HeaderGroup[]
	expand?: (rowData: any) => void
	collapse?: (rowData: any) => void
	onGroupClick?: (groupId: string) => void

	interactionManager: InteractionManager

	errorsMap?: TableErrorsMap
}

export const BaseTable = ({
	columns, rowsData, footerRowsData, cellEdited, footerCellEdited, refetching,
	pagination, compact, filterSortPage, disabledReorder, fill,
	additionalHeaderRow, expand, collapse, onGroupClick, interactionManager, errorsMap
}: Props) => {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const [activeFilterColumn, setActiveFilterColumn] = useState<GenericColumnModel>();

	const { columnWidthMap, setColumnWidthMap } = useColumnWidths(columns, rowsData, containerRef, fill);

	const setFiltersCallback = useCallback(
		(newFilters: FilterType[]) => {
			interactionManager.filter?.(newFilters);
			setActiveFilterColumn(undefined);
		},
		[interactionManager]
	)

	const cancelFilterCallback = useCallback(
		() => setActiveFilterColumn(undefined),
		[]
	)

	const onFilterClickCallback = useCallback(
		(columnId: string) => {
			const selectedColumn = columns.find((column) => column.id === columnId);
			setActiveFilterColumn(selectedColumn);
		},
		[columns]
	)

	// created just for useRemoteColumns to do its magic for union of rowsData and active filters
	const fakeRowsData = useMemo(
		() => {
			const rows = [...rowsData];
			for (let filter of filterSortPage.filters || []) {
				if (filter.columnId) {
					const column = columns.find((item) => item.id === filter.columnId);
					if (column instanceof RemoteOptionsColumnModel) {
						rows.push({ [filter.columnId]: [filter.value] });
					} else if (column instanceof RemoteOptionColumnModel) {
						rows.push({ [filter.columnId]: filter.value });
					}
				}

				for (let orFilter of filter.orFilters || []) {
					if (orFilter.columnId) {
						const column = columns.find((item) => item.id === orFilter.columnId);
						if (column instanceof RemoteOptionsColumnModel) {
							rows.push({ [orFilter.columnId]: [orFilter.value] });
						} else if (column instanceof RemoteOptionColumnModel) {
							rows.push({ [orFilter.columnId]: orFilter.value });
						}
					}
				}
			}

			return rows;
		},
		[columns, filterSortPage.filters, rowsData]
	)

	const { fetchingColumns, columnItemsMap } = useRemoteColumns(columns, fakeRowsData);

	const convertedColumns: GenericColumnModel[] = useMemo(
		() => columns.map((column) => {
			if (column instanceof RemoteOptionsColumnModel || column instanceof RemoteOptionColumnModel) {
				// to perserve class type
				const clonedColumn = cloneColumnModel(column);
				(clonedColumn as RemoteOptionColumnModel<any>).items = columnItemsMap[column.id] || emptyArray;
				(clonedColumn as RemoteOptionColumnModel<any>).fetching = fetchingColumns.includes(column.id);
				return clonedColumn;
			}
			return column;
		}),
		[fetchingColumns, columnItemsMap, columns]
	)

	return (
		<div>
			<Filter
				filters={filterSortPage.filters || []}
				column={activeFilterColumn}
				columns={convertedColumns}
				onSave={setFiltersCallback}
				onCancel={cancelFilterCallback}
			/>
			<div ref={containerRef} className={styles.container}>
				<Header
					columns={convertedColumns}
					columnWidthMap={columnWidthMap}
					filterSortPage={filterSortPage}
					onFilter={onFilterClickCallback}
					disabledReorder={disabledReorder}
					additionalHeaderRow={additionalHeaderRow}
					interactionManager={interactionManager}
				/>
				{rowsData.length > 0 ?
					<>
						<Body
							columns={convertedColumns}
							rowsData={rowsData}
							columnWidthMap={columnWidthMap}
							cellEdited={cellEdited}
							expand={expand}
							collapse={collapse}
							onGroupClick={onGroupClick}
							interactionManager={interactionManager}
							errorsMap={errorsMap}
						/>
						<Footer
							columns={convertedColumns}
							columnWidthMap={columnWidthMap}
							footerRowsData={footerRowsData}
							footerCellEdited={footerCellEdited}
						/>
						<Resize
							columns={convertedColumns}
							columnWidthMap={columnWidthMap}
							setColumnWidthMap={setColumnWidthMap}
							interactionManager={interactionManager}
						/>
						<Frozen
							columns={convertedColumns}
							columnWidthMap={columnWidthMap}
							containerRef={containerRef}
						/>
					</>
					:
					<div className={styles.empty_table_container}>
						<div className={styles.empty_table_inner}>
							<div className={styles.empty_table_message}>
								<Translation i18n='i18n.notifications.noData' />
							</div>
						</div>
					</div>
				}
			</div>
			{pagination && rowsData.length > 0 &&
				<Pagination
					offset={pagination.offset || 0}
					limit={pagination.limit || defaultPaginationLimit}
					count={pagination.count}
					onChange={pagination.onChange}
					compact={compact}
				/>
			}
			{refetching &&
				<OverlaySpinner
					size={60}
					useBrandColor
					withBackground
				/>
			}
		</div>
	)
}
